import type { TransformComponent3 } from '@seddi/scene';
import { Object3D, Quaternion, Vector3 } from '@seddi/three';

import { create as createObject, removeFromParent } from '../core/object3d';
import { ResourcesContext } from '../resources/resourcesContext';

import { type GarmentRenderable, dispose as disposeGarment } from './garmentRenderable';
import { type LightRenderable, dispose as disposeLight } from './lightRenderable';
import { type MeshRenderable, dispose as disposeMesh } from './meshRenderable';
import { type ModelRenderable, dispose as disposeModel } from './modelRenderable';
import { type PieceRenderable, dispose as disposePiece } from './pieceRenderable';
import { type SeamRenderable, dispose as disposeSeam } from './seamRenderable';

export interface EntityRenderable {
  readonly id: string;
  children: EntityRenderable[];
  garment?: GarmentRenderable;
  light?: LightRenderable;
  mesh?: MeshRenderable;
  model?: ModelRenderable;
  readonly node: Object3D;
  piece?: PieceRenderable;
  seam?: SeamRenderable;
}

/**
 * Create renderable.
 * @param id - Entity ID.
 * @param parent - Parent renderable.
 */
export const create = (id: string, parent?: EntityRenderable): EntityRenderable => {
  const node = createObject(id, parent?.node);
  return { id, children: [], node };
};

/**
 * Dispose renderable.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 * @param onDispose - Renderable.
 */
export const dispose = (
  renderable: EntityRenderable,
  resources: ResourcesContext,
  onDispose?: (entity: EntityRenderable) => void,
) => {
  // Depth-first removai.
  for (const child of renderable.children) {
    dispose(child, resources, onDispose);
  }

  // Components.
  if (renderable.light) {
    disposeLight(renderable.light);
  }
  if (renderable.mesh) {
    disposeMesh(renderable.mesh, resources);
  }
  if (renderable.model) {
    disposeModel(renderable.model, resources);
  }
  if (renderable.piece) {
    disposePiece(renderable.piece, resources);
  }
  if (renderable.seam) {
    disposeSeam(renderable.seam, resources);
  }
  if (renderable.garment) {
    disposeGarment(renderable.garment, resources);
  }

  // Remove.
  removeFromParent(renderable.node);

  // Visitor callback.
  if (onDispose) {
    onDispose(renderable);
  }
};

/**
 * Update node matrix.
 * @param node - 3D node.
 * @param transform - Transform component.
 */
export const updateTransform = (() => {
  const PositionDefault = new Vector3(0, 0, 0);
  const RotationDefault = new Quaternion(0, 0, 0, 1);
  const ScaleDefault = new Vector3(1, 1, 1);

  return (renderable: EntityRenderable, transform: TransformComponent3) => {
    const node = renderable.node;
    if (transform.position) {
      node.position.copy(transform.position);
    } else {
      node.position.copy(PositionDefault);
    }

    if (transform.rotation) {
      node.quaternion.copy(transform.rotation);
    } else {
      node.quaternion.copy(RotationDefault);
    }

    if (transform.scale) {
      node.scale.copy(transform.scale);
    } else {
      node.scale.copy(ScaleDefault);
    }

    node.updateMatrix();
  };
})();
